import React, { useState } from 'react';
import { Alert, Col, Divider, Form, Input, Row, Typography } from 'antd';
import { createMessage } from 'works/legacy/lib/message-helper';
import { Spacing } from 'works/legacy/lib/style-helper';
import { Icon, SubmitButton } from '@common/components';
import { gql, useMutation } from '@common/hooks';
import { Store } from 'antd/lib/form/interface';
import { PasswordValidationFeedback } from '@identity/components';
import { passwordValidator } from '@identity/services';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';

const changePasswordMutation = gql`
  mutation ChangeWorksUserPassword($input: ChangeWorksUserPasswordInput!) {
    changeWorksUserPassword(input: $input) {
      worksUser {
        id
        firstName
      }
    }
  }
`;

export const ChangePasswordSection = () => {
  useSetBreadcrumbs([
    {
      name: 'Account Settings',
      path: '/works/account-settings',
    },
    {
      name: 'Login & Password',
      path: '/works/account-settings/change-password',
    },
  ]);
  const [newPassword, setNewPassword] = useState('');
  const [form] = Form.useForm();
  const [changeWorksUserPassword] = useMutation(changePasswordMutation);

  const onFinish = (values: Store) => {
    changeWorksUserPassword({
      variables: {
        input: {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        },
      },
    }).then((res) => {
      if (res.errors) {
        createMessage(
          'error',
          'Could not change your password. Check your current password.'
        );
      } else {
        form.resetFields();
        createMessage('success', 'Password successfully changed.');
      }
    });
  };

  return (
    <div>
      <Typography.Title
        level={3}
        style={{ fontSize: '18px', margin: '24px 0', fontWeight: 500 }}
      >
        Login & Password
      </Typography.Title>
      <Divider />
      <Alert
        message="Passwords are not applicable for accounts that only allow Single Sign On (SSO) logins."
        type="warning"
        style={{ marginBottom: Spacing.baseSpacingPx }}
      />
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Row>
          <Col span={24}>
            <Form.Item
              name="currentPassword"
              label="Current Password"
              rules={[
                {
                  required: true,
                  message: 'Please enter your current password.',
                },
              ]}
            >
              <Input.Password
                prefix={<Icon icon="unlock" kind="light" />}
                type="password"
                placeholder="Type your current password"
                size="large"
                autoComplete="off"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col span={12}>
            <Form.Item
              name="newPassword"
              label="New Password"
              rules={[
                {
                  required: true,
                  message: 'Please create your new password.',
                },
                {
                  validator: (_, value) => {
                    if (passwordValidator(value)) {
                      return Promise.reject('Password must include:');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.Password
                prefix={<Icon icon="lock" kind="light" />}
                type="password"
                placeholder="Type the new password"
                size="large"
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="off"
              />
            </Form.Item>
            <PasswordValidationFeedback password={newPassword} />
            <div style={{ marginBottom: 16 }} />
          </Col>
          <Col span={12}>
            <Form.Item
              label="Confirm Password"
              name="newPasswordConfirmation"
              rules={[
                {
                  required: true,
                  message: 'Please confirm your new password.',
                },
                {
                  validator: (_, value) => {
                    if (value && value.length > 0 && newPassword !== value) {
                      return Promise.reject(
                        'Password does not match what you entered initially.'
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.Password
                prefix={<Icon icon="lock" kind="light" />}
                type="password"
                size="large"
                placeholder="Type the new password, again"
                autoComplete="off"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <SubmitButton>Update Password</SubmitButton>
        </Form.Item>
      </Form>
    </div>
  );
};
